/**
 * [LGP 2026-10-15, user] NEW FILE — shared pieces of the Lead Generator Performance tab (LeadGeneratorPerformancePage.tsx and
 * its five views). The helpers are the Apps Script page's own (JavaScript.html — the function named in each comment); the
 * styled parts are this app's look (layout may differ, the user said; the logic may not).
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { lgButtonPrimary, lgButtonSecondary, lgSunken, lgTile, lgOnWhite, lgSquircle, LG_PRESS, LgPressProps } from '../../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { reducedMotion } from '../../styles/motion';
import { SlideViewport } from '../../components/ViewSlide';
import type { LgpBonusRule } from '../../services/api';

export const dark = (p: { theme: DefaultTheme }) => p.theme.colorScheme === 'dark';

/** [Code.js MIN_DATE] */
export const MIN_DATE = '2026-01-01';
export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Today in India (the sheet's time zone). */
const IST_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' });
export const todayIst = () => IST_DAY.format(new Date());
const pad2 = (n: number) => (n < 10 ? '0' : '') + n;

/** 'YYYY-MM-DD' of a calendar day (m0 = month index, may run over: 12 → January of the next year, day 0 = the month before's last). */
const ymd = (y: number, m0: number, d: number) => new Date(Date.UTC(y, m0, d)).toISOString().slice(0, 10);

/** [Code.js getDefaultRange] 21st of the previous month → 20th of the current month (India); never below MIN_DATE.
 *  month = Monthly Performance's default ("Up to month" = this month).
 *  [LGP-CYCLE 2026-10-27, user] "make the start end date same like we have in the team sameer report, so that it will auto gets
 *  changes as per the logic and we can use it manually also": start / end = the 21st-to-20th work month that HOLDS today, as the
 *  Team Sameer Report's cycleOf — up to the 20th: last month 21 → this month 20; from the 21st: this month 21 → next month 20
 *  (was always last month 21 → this month 20, so from the 21st it showed the work month that had ended). month is unchanged. */
export function defaultRange(): { start: string; end: string; month: string } {
  const [y, m, d] = todayIst().split('-').map(Number);
  let start = d >= 21 ? ymd(y, m - 1, 21) : ymd(y, m - 2, 21);
  let end = d >= 21 ? ymd(y, m, 20) : ymd(y, m - 1, 20);
  if (start < MIN_DATE) start = MIN_DATE;
  if (end < MIN_DATE) end = MIN_DATE;
  return { start, end, month: `${y}-${pad2(m)}` };
}
/** [LGP-CYCLE 2026-10-27] The last day the End date offers: the end of next month (the Team Sameer Report's lastDayOffered), so
 *  the whole work month on show can be picked. */
export const lastDayOffered = (): string => { const [y, m] = todayIst().split('-').map(Number); return ymd(y, m + 1, 0); };

/** [JavaScript.html prettyDate] '2026-08-20' -> '20 Aug 2026' (the server's prettyDate_). */
export function prettyDate(iso: string): string {
  const p = String(iso || '').split('-');
  if (p.length !== 3) return '';
  return `${p[2]} ${MONTH_NAMES[Number(p[1]) - 1]} ${p[0]}`;
}
/** [JavaScript.html rangeLabel] '21 Jul 2026 - 20 Aug 2026' — a reversed range is turned round, as the server does. */
export function rangeLabel(startIso: string, endIso: string): string {
  if (startIso > endIso) { const t = startIso; startIso = endIso; endIso = t; }
  return `${prettyDate(startIso)} - ${prettyDate(endIso)}`;
}
/** [JavaScript.html monthsEndingAt] The three months ending at 'yyyy-MM' (labels 'Aug-2026'). */
export function monthsEndingAt(iso: string): { label: string }[] {
  let y: number; let m: number;
  if (/^\d{4}-\d{2}$/.test(iso || '')) { y = Number(iso.slice(0, 4)); m = Number(iso.slice(5, 7)); } else {
    const [ty, tm] = todayIst().split('-').map(Number); y = ty; m = tm;
  }
  const out: { label: string }[] = [];
  for (let back = 2; back >= 0; back--) {
    let yy = y; let mm = m - back;
    while (mm < 1) { mm += 12; yy--; }
    out.push({ label: `${MONTH_NAMES[mm - 1]}-${yy}` });
  }
  return out;
}

/** [JavaScript.html fmtBonus] 2000 -> '₹2,000'; zero renders as a dash. */
export const fmtBonus = (v: number) => (v ? `₹${Number(v).toLocaleString('en-IN')}` : '—');
/** [JavaScript.html fmtInt / fmtPct / fmtUsd] */
export const fmtInt = (v: number) => (v || 0).toLocaleString('en-US');
export const fmtPct = (v: number) => `${((v || 0) * 100).toFixed(2)}%`;
export const fmtUsd = (v: number) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** [JavaScript.html teamOwnerFirstName] 'Team Ankit' -> 'ankit'; '' for groups not named after someone (House, Team
 *  Freelancer, Not in roster). */
export function teamOwnerFirstName(team: string): string {
  const m = /^team\s+(.+)$/i.exec(String(team || '').trim());
  if (!m) return '';
  const who = m[1].trim().toLowerCase();
  if (who === 'freelancer' || who === 'freelancers') return '';
  return who;
}
/** [JavaScript.html ownerFirst] Moves the team's owner to the top of their own section (FIRST NAME match only, so 'Team Ankit'
 *  finds 'Ankit Pal' and not 'Ankur Singh'); everyone else keeps the current sort. Returns a new list. */
export function ownerFirst<T extends { name: string }>(members: T[], team: string): T[] {
  const owner = teamOwnerFirstName(team);
  const list = members.slice();
  if (!owner) return list;
  for (let i = 0; i < list.length; i++) {
    const first = String(list[i].name || '').trim().split(/\s+/)[0].toLowerCase();
    if (first === owner) {
      if (i > 0) list.unshift(list.splice(i, 1)[0]);
      break;
    }
  }
  return list;
}

/** [JavaScript.html bonusFor] = the server's bonusFor_. A blank `to` means "and above". */
export function bonusFor(finalCount: number, rules: (LgpBonusRule | { from: unknown; to: unknown; amount: unknown })[]): number {
  for (const r of rules) {
    const to = (r.to === null || r.to === undefined || r.to === '') ? null : Number(r.to);
    if (finalCount >= Number(r.from) && (to === null || finalCount <= to)) return Number(r.amount) || 0;
  }
  return 0;
}

/** [JavaScript.html parseVersion] '<ds>|r12|b7' -> {ds, r: 12, b: 7}. */
export function parseVersion(v: string): { ds: string; r: number; b: number } {
  const parts = String(v || '').split('|');
  const b = Number(String(parts.pop() || '').replace(/^b/, '')) || 0;
  const r = Number(String(parts.pop() || '').replace(/^r/, '')) || 0;
  return { ds: parts.join('|'), r, b };
}

/** A server refusal as people should read it (the server's own wording). */
export const errorText = (e: any): string => e?.response?.data?.error || e?.message || 'Something went wrong. Please try again.';

/** The text compare the page uses for names and emails (the same on every computer). */
export const textCompare = (a: string, b: string) => String(a).localeCompare(String(b), 'en');

/* -------------------------------------------------------------------------- */
/* Look                                                                        */
/* -------------------------------------------------------------------------- */

/** The dock's reach (bottom 18 + about 68 tall); the Leads type has no dock (AppLayout [LEADS-NO-DOCK]) → 16. */
export const DOCK_CLEAR = 88;

/** One view: its header line, then (tiles,) the toolbar, then the table filling the rest. */
export const ViewWrap = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* [PHONE-LAYOUT 2026-10-23, user] phones: the view at its full height — the page scrolls, nothing is fixed at the top
     (the US | ROW slide box below then takes its table's height too) */
  @media (max-width: 768px) { height: auto; }
`;

/** The view's line: what it shows (left), its controls (right). */
export const ViewBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 0 4px;

  .sub { font-size: 13px; font-weight: 600; color: ${(p) => p.theme.dim}; padding-bottom: 8px; }
  .controls { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; }

  /* [PHONE-LAYOUT 2026-10-23] phones: the caption on its own line, then the controls in two equal columns — the two dates
     side by side; an odd last control (+ Add member, Save changes, Monthly's month alone) takes the whole line
     ([LGP-CYCLE 2026-10-27] the Apply buttons are gone: a picked date / month loads at once) */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    .sub { padding-bottom: 0; }
    .controls {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-items: end;
      gap: 10px;
    }
    .controls > :last-child:nth-child(odd) { grid-column: 1 / -1; }
    .controls > button { width: 100%; }
  }
`;

/** A labelled control (the script's .field: label above the box). */
export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;

  > span { font-size: 11px; font-weight: 700; letter-spacing: 0.3px; text-transform: uppercase; color: ${(p) => p.theme.dim}; }

  /* [PHONE-LAYOUT 2026-10-23] phones: the date / month box across its column */
  @media (max-width: 768px) {
    min-width: 0;
    & > button { width: 100%; }
  }
`;

/* [LIQUID-GLASS 2026-11-02, user] the glossy green / glass capsules (styles/liquidGlass.ts) at the old size (38 px, 14 px sides);
   the plain one keeps a thin edge in light (it sits on the white card) */
export const PrimaryBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
  height: 38px;
  padding: 0 14px;
`;
export const SecondaryBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  ${lgOnWhite}
  height: 38px;
  padding: 0 14px;
`;

/** Search (and the US | ROW switch) left, the status line right. */
export const Toolbar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  padding: 0 4px;

  .left { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

  /* [PHONE-LAYOUT 2026-10-23] phones: the search across the whole line, the switch centred under it, then the status line
     (full width, from the left) */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    .left { flex-direction: column; align-items: stretch; }
    .left > [data-seg] { align-self: center; }
  }
`;

/* [LIQUID-GLASS 2026-11-02, user] pressed into the glass, a capsule; a soft green ring and glow while typing (the Payments search) */
export const SearchInput = styled.input`
  height: 36px;
  width: 280px;
  max-width: 100%;
  padding: 0 14px;
  border-radius: 999px;
  ${lgSunken}
  color: ${(p) => p.theme.text};
  font: inherit;
  font-size: 13px;
  outline: none;

  &::placeholder { color: ${(p) => p.theme.dim}; }
  &:disabled { opacity: 0.55; }

  /* [PHONE-LAYOUT 2026-10-23] phones: the whole line, 40 px tall (the 280 px box cut Team Roster's hint) */
  @media (max-width: 768px) { width: 100%; height: 40px; }
`;

export const StatusLine = styled.span<{ $error?: boolean }>`
  font-size: 12.5px;
  font-weight: 600;
  color: ${(p) => (p.$error ? p.theme.danger : p.theme.dim)};
  text-align: right;

  /* [PHONE-LAYOUT 2026-10-23] phones: from the left (right-aligned, a wrapped line read ragged) */
  @media (max-width: 768px) { text-align: left; }
`;

/** The table's box: scrolls by itself, its own bottom pad clears the dock. */
export const TableScroll = styled.div<{ $noDock?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-bottom: ${(p) => (p.$noDock ? 16 : DOCK_CLEAR)}px;
  overscroll-behavior: contain;

  th, td { white-space: nowrap; }
  tbody tr[data-row-index] { cursor: default; }

  /* [PHONE-LAYOUT 2026-10-23, user] phones: as tall as its rows (no box of its own to scroll up / down in) — it only swipes
     sideways; the page scrolls. The end pad still lifts the last row above the dock. */
  @media (max-width: 768px) {
    flex: none;
    min-height: 0;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto;
  }
`;

/* [LGP-SLIDE 2026-10-15] A switch's views side by side for the slide (Leads Count US | ROW, Monthly Performance Overall |
   US | ROW): the one on show in place, the others just off to their side (components/ViewSlide). Each pane is a column whose
   table box fills it and scrolls by itself, so every view keeps its own scroll position. */
export const SideViewport = styled(SlideViewport)`
  flex: 1;
  min-height: 0;

  & > * {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

export const Muted = styled.span`
  color: ${(p) => p.theme.dim};
`;

/** The NAL number in red (the script's .nal), a 0 shown blank by the script. */
export const NalNum = styled.span`
  color: ${(p) => p.theme.danger}; /* [PURE-INK] the theme's red: light #c81e1e (5 : 1 on the grey lines), dark #f87171 */
  font-weight: 700;
`;

/** The row tints: a team's banner, a team Total, the Grand Total. */
export const lineBg = (kind: string | undefined, isDark: boolean): string | undefined => {
  if (kind === 'head') return isDark ? 'rgba(255,255,255,0.055)' : 'rgba(22,25,23,0.05)';
  if (kind === 'sub') return isDark ? 'rgba(255,255,255,0.03)' : 'rgba(22,25,23,0.025)';
  if (kind === 'grand') return isDark ? 'rgba(74,205,139,0.14)' : 'rgba(26,159,125,0.10)';
  return undefined;
};

/** The small two-way switch (the script's .seg): the US | ROW switch. [LGP-SLIDE 2026-10-15, user] "when i click us and row
 *  it should show left right animation": the green knob slides from one side to the other (the app's knob pill — this tab's
 *  view switch, Lead Management's KnobPill), while the table below slides with it (LeadsCountView). */
/* [LIQUID-GLASS 2026-11-02, user] the track is pressed into the glass, the knob glossy (a shine + a light top edge) and it slides
   the theme switch's way (0.42 s, the same curve) */
const SegWrap = styled.div`
  position: relative;
  display: inline-flex;
  padding: 3px;
  border-radius: 999px;
  border: 1px solid transparent;
  background: ${(p) => (dark(p) ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  box-shadow: ${(p) => (dark(p)
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)')};
  &[data-disabled='1'] { opacity: 0.55; }
`;
const SegKnob = styled.span<{ $left: number; $width: number; $ready: boolean }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  width: ${(p) => p.$width}px;
  transform: translateX(${(p) => p.$left}px);
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), ${(p) => (dark(p) ? '#4acd8b' : p.theme.green600)};
  box-shadow: 0 2px 8px ${(p) => (dark(p) ? 'rgba(74,205,139,0.30)' : 'rgba(20,107,84,0.30)')}, inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.12);
  opacity: ${(p) => (p.$width ? 1 : 0)};
  pointer-events: none;
  /* Off until the knob has been drawn in place once: no slide-in when the view first shows. */
  transition: ${(p) => (p.$ready ? 'transform 0.42s cubic-bezier(0.32, 0.72, 0, 1), width 0.42s cubic-bezier(0.32, 0.72, 0, 1)' : 'none')};
  ${reducedMotion} { transition: none; }
`;
const SegBtn = styled.button<{ $on: boolean }>`
  position: relative;
  z-index: 1;
  border: none;
  border-radius: 999px;
  padding: 6px 16px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
  background: transparent;
  color: ${(p) => (p.$on ? (dark(p) ? '#06281d' : '#ffffff') : p.theme.dim)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);
  &:disabled { cursor: not-allowed; }
`;
export function Seg<K extends string>({ value, options, onPick, disabled, label }: {
  value: K; options: { key: K; label: string }[]; onPick: (k: K) => void; disabled?: boolean; label: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<Partial<Record<K, HTMLButtonElement | null>>>({});
  const [knob, setKnob] = useState({ left: 0, width: 0 });
  const [ready, setReady] = useState(false);
  const measure = useCallback(() => {
    const el = btnRefs.current[value];
    if (el) setKnob((k) => (k.left === el.offsetLeft && k.width === el.offsetWidth ? k : { left: el.offsetLeft, width: el.offsetWidth }));
  }, [value]);
  useLayoutEffect(() => { measure(); }, [measure]);
  // The labels can change size (the font arriving): measure again.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !wrapRef.current) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [measure]);
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <SegWrap ref={wrapRef} role="group" aria-label={label} data-seg={label} data-disabled={disabled ? '1' : undefined}>
      <SegKnob $left={knob.left} $width={knob.width} $ready={ready} aria-hidden="true" data-seg-knob="" />
      {options.map((o) => (
        <SegBtn key={o.key} ref={(el) => { btnRefs.current[o.key] = el; }} type="button" $on={o.key === value} aria-pressed={o.key === value}
          disabled={disabled} onClick={() => onPick(o.key)} data-seg-key={o.key}>{o.label}</SegBtn>
      ))}
    </SegWrap>
  );
}

/** Summary tiles (the script's .tiles). [LGP-DUP 2026-10-16] five in a row: + Duplicate Leads after NAL Leads. */
export const Tiles = styled.div`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr)); /* [LGP-NO-DUP 2026-10-27] 4 tiles again (5 with [LGP-DUP]'s Duplicate Leads) */
  gap: 10px;
  padding: 0 4px;
  @media (max-width: 768px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;
/* [LIQUID-GLASS 2026-11-02, user] glass cards (styles/liquidGlass.ts lgTile, smooth corners); the main tile keeps its green, glossy */
export const Tile = styled.div<{ $main?: boolean }>`
  padding: 10px 14px;
  ${lgSquircle(12)}
  ${lgTile}
  ${(p) => (p.$main ? `
    background-color: ${dark(p) ? 'rgba(74,205,139,0.10)' : 'rgba(26,159,125,0.08)'};
    border-color: ${dark(p) ? 'rgba(74,205,139,0.50) rgba(74,205,139,0.30) rgba(74,205,139,0.36)' : 'rgba(26,159,125,0.30) rgba(26,159,125,0.28) rgba(26,159,125,0.34)'};
  ` : '')}

  .l { font-size: 10px; font-weight: 800; letter-spacing: 0.4px; text-transform: uppercase; color: ${(p) => p.theme.dim}; }
  .v { margin-top: 4px; font-size: 20px; font-weight: 800; font-variant-numeric: tabular-nums; color: ${(p) => p.theme.text}; min-height: 24px; }
  .v.nal { color: ${(p) => p.theme.danger}; }
  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) { .l { font-size: 11px; } }
  /* [PHONE-LAYOUT 2026-10-23] phones (two tiles a line): the fifth, Final Lead Count, takes the whole last line.
     [LGP-NO-DUP 2026-10-27] only an odd last tile takes the whole line — with 4 tiles they sit 2 × 2 (no gap beside NAL Leads) */
  @media (max-width: 768px) { &:last-child:nth-child(odd) { grid-column: 1 / -1; } }
`;

/** The script's chip (NAL Status). */
export const Chip = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${(p) => (dark(p) ? '#fca5a5' : '#b91c1c')};
  background: ${(p) => (dark(p) ? 'rgba(248,113,113,0.14)' : 'rgba(220,38,38,0.10)')};
`;

export const linkCss = css`
  color: ${(p) => p.theme.greenInk};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;
export const CellLink = styled.a`
  ${linkCss}
`;

/* -------------------------------------------------------------------------- */
/* The page ↔ view contract                                                   */
/* -------------------------------------------------------------------------- */

/** [JavaScript.html PAGES] The five views, in the dock's order. */
export type ViewId = 'leads-count' | 'leads-analysis' | 'nal-records' | 'team-roster' | 'bonus-rules';
/** A roster person as the roster now has them — what the instant patches read (the script's patchLeadsPerson argument). */
export interface PersonPatch { email: string; name: string; leader: string; status: string; rowStatus: string }
/** What a view lets the page do: fetch again in the background, and (Leads Count / Monthly Performance) apply a roster or
 *  bonus change at once. */
export interface ViewHandle {
  refresh: () => void;
  patchPerson?: (p: PersonPatch) => void;
  patchBonus?: (rules: { from: unknown; to: unknown; amount: unknown }[]) => void;
}
export interface ViewProps {
  active: boolean;
  noDock: boolean;
  /** per-person browser memory (the US | ROW choice) */
  storageKey: string;
  /** every response's version — the page re-fetches the views that depend on the part that moved */
  onVersion: (v: string, from: ViewId) => void;
  register: (id: ViewId, handle: ViewHandle) => void;
}

/** A team banner's text: the team, then "· N people" (the script's .team-head + .count). */
export const TeamHead: React.FC<{ team: string; count: number }> = ({ team, count }) => (
  <b>{team}<Muted style={{ fontWeight: 600 }}>{` · ${count} ${count === 1 ? 'person' : 'people'}`}</Muted></b>
);
